import React, { useContext, useMemo, useState } from 'react';

import {
  Badge,
  Box,
  Button,
  ColumnLayout,
  Container,
  Header,
  Icon,
  Link,
  Modal,
  SpaceBetween,
  Spinner,
  StatusIndicator,
  Table,
  Toggle,
} from '@amzn/awsui-components-react-v3';

import { Context } from '../../store';
import { AsyncState, ReceivedLineItem, ReceiveExecutionStatus, ReceiveMaterialAsyncResponse } from '../../store/types';
import { ValueWithLabel } from '../Utils';
import { EXECUTION_ERROR_STATES, EXECUTION_STATE_MESSAGES } from './constants';

interface ReceivingPartsProps {
  errors: any[];
  succeded: any[];
  pendingList: any[];
  executionId?: string;
}

const PartStatus = ({ part }: any) => {
  const status = useMemo(() => {
    if (EXECUTION_ERROR_STATES.includes(part?.serialStatus)) {
      return AsyncState.ERROR;
    }

    if (part?.serialStatus === ReceiveExecutionStatus.SUCCESSFULLY_COMPLETED_RECEIVE) {
      return AsyncState.OK;
    }

    return AsyncState.IN_PROGRESS;
  }, [part?.serialStatus]);

  const stateMessage = EXECUTION_STATE_MESSAGES[part?.serialStatus];
  return (
    <Box margin={{ left: 's' }} variant="span">
      {status === AsyncState.IN_PROGRESS && (
        <Badge>
          <Spinner size="normal" />{' '}
          {stateMessage ? `${AsyncState.IN_PROGRESS}: ${stateMessage}` : AsyncState.IN_PROGRESS}
        </Badge>
      )}
      {status === AsyncState.OK && (
        <Badge color="green">
          <Icon name="status-positive" /> Received
        </Badge>
      )}
      {status === AsyncState.ERROR && (
        <Badge color="red">
          <Icon name="status-negative" /> {stateMessage ? stateMessage : 'Errored'}
        </Badge>
      )}
    </Box>
  );
};

export const ExecutionDetails = ({
  errors = [],
  succeded = [],
  pendingList = [],
  executionId,
}: ReceivingPartsProps) => {
  const [visible, setVisible] = useState(false);
  const [onlyErrors, setOnlyErrors] = useState(false);

  const { state } = useContext(Context);

  const receiveReference: ReceiveMaterialAsyncResponse = state?.receiveReferenceList?.find(
    (ref: ReceiveMaterialAsyncResponse) => ref?.receiveExecutionUUId === executionId,
  );

  const receivedLineItem: ReceivedLineItem = receiveReference?.receivedLineItemList?.[0];

  const orderedSerials = onlyErrors
    ? errors
    : [...errors.map((e) => ({ ...e, state: 'error' })), ...pendingList, ...succeded];

  return (
    <Box variant="span" margin={{ left: 's' }}>
      <Link variant="primary" onFollow={() => setVisible(true)} href="#">
        <div className="part-status">
          <SpaceBetween direction="horizontal" size="s">
            <StatusIndicator type="in-progress">{pendingList?.length} In Progress </StatusIndicator>
            <StatusIndicator type="error">{errors?.length} Errored </StatusIndicator>
            <StatusIndicator type="success">{succeded?.length} Received </StatusIndicator>
          </SpaceBetween>
        </div>
      </Link>
      {visible && (
        <Modal
          onDismiss={() => setVisible(false)}
          visible={visible}
          size="max"
          footer={
            <Box float="right">
              <Button variant="link">Cancel</Button>
            </Box>
          }
          header={`Receive Status`}
        >
          <Container>
            <ColumnLayout columns={3} variant="text-grid">
              <SpaceBetween size="l">
                <ValueWithLabel label="MPN">{receivedLineItem?.manufacturerPartNumber}</ValueWithLabel>
                <ValueWithLabel label="IPN">{receivedLineItem?.internalPartNumber}</ValueWithLabel>
              </SpaceBetween>
              <SpaceBetween size="l">
                <ValueWithLabel label="Received At">
                  {new Date(receiveReference?.receiveScanEndTime).toLocaleDateString('en-US')}-
                  {new Date(receiveReference?.receiveScanEndTime).toLocaleTimeString()}
                </ValueWithLabel>
                <ValueWithLabel label="Shipment ID">{receiveReference?.externalScannableId}</ValueWithLabel>
              </SpaceBetween>
              <SpaceBetween size="l">
                <ValueWithLabel label={`Total Receiving (${succeded.length + errors?.length + pendingList?.length})`}>
                  <SpaceBetween direction="horizontal" size="s">
                    <StatusIndicator type="in-progress">{pendingList?.length} In Progress </StatusIndicator>
                    <StatusIndicator type="error">{errors?.length} Errored </StatusIndicator>
                    <StatusIndicator type="success">{succeded?.length} Received </StatusIndicator>
                  </SpaceBetween>
                </ValueWithLabel>
              </SpaceBetween>
            </ColumnLayout>
            <Box margin={{ top: 's' }}>
              <hr />
            </Box>
            <Table
              variant="embedded"
              header={
                <Header
                  variant="h3"
                  actions={
                    <Box margin={{ top: 'xs' }}>
                      <Toggle checked={onlyErrors} onChange={({ detail }) => setOnlyErrors(detail?.checked)}>
                        Show only parts with errors
                      </Toggle>
                    </Box>
                  }
                >
                  {`Receiving Parts (${succeded.length + errors?.length + pendingList?.length})`}
                </Header>
              }
              columnDefinitions={[
                {
                  id: 'serialID',
                  header: 'serialID',
                  cell: (item) => item.externalSerialId,
                },
                {
                  id: 'siteSpecificSerialId',
                  header: 'site Specific SerialID',
                  cell: (item) => (item.siteSpecificSerialId ? '-' : item.siteSpecificSerialId),
                },
                {
                  id: 'status',
                  header: 'status',
                  cell: (item) => {
                    return <PartStatus part={item} />;
                  },
                },
              ]}
              items={orderedSerials}
            />
          </Container>
        </Modal>
      )}
    </Box>
  );
};
